import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import {
	Button,
	Datepicker,
	DynamicIconWrapper,
	IconButton,
	Icons,
	Select,
	SliderCheckbox,
	Tooltip,
} from '@components/common';
import ActionsMenu from '@components/common/ActionsMenu';
import Gantt from '@components/common/Gantt';
import Switcher from '@components/common/Switcher';
import MultiSearch from '@components/common/Table/MultiSearch';

import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ITableDatepickerFilter,
	ITableSearch,
	TSetStateTableFilter,
	TSetStateTableFilterMulti,
	TTableFilter,
} from '@interfaces/components/table';
import { GanttHandle, Task } from '@interfaces/ganttTasks/ganttTasks';

import { EGanttTaskType, EViewMode } from '@enums/ganttTasks';
import { EAddTypeId } from './enums';

import { TableFilterTypes } from '@constants/table';
import { addActions, timelineViewModeOptions, VIEW_SWITCHER_BREAKPOINT } from './constants';
import { defaultColumnWidth, defaultQuarterYearColumnWidth } from '@components/common/Gantt/constants';
import { resetFilterTooltipText } from '@components/common/Table/constants';

import CloseButton from '../CloseButton';
import Label from '../Label';

import styles from './GanttTaskTable.module.scss';

interface IGanttTaskTableProps {
	data: Task[];
	columnWidth?: number;
	emptyText?: string;
	search?: Array<ITableSearch>;
	searchThreshold?: number;
	searchWidth?: string;
	isOpenSearch?: boolean;
	isLoading: boolean;
	isWithoutFilter?: boolean;
	isOpenFilters?: boolean;
	filters?: Array<TTableFilter>;
	dateFilters?: Array<ITableDatepickerFilter>;
	isClearableDatepicker?: boolean;
	handleResetFilters?: () => void;
	isCreatable?: boolean;
	handleAdd?: (type: EAddTypeId) => void;
	onTaskClick?: (task: Task) => void;
	onMilestoneClick?: (task: Task) => void;
}

const GanttTaskTable = ({
	data,
	columnWidth,
	emptyText = 'No results.',
	search,
	searchThreshold,
	searchWidth,
	isOpenSearch = false,
	isLoading,
	isWithoutFilter = false,
	isOpenFilters = false,
	filters = [],
	dateFilters = [],
	isClearableDatepicker = true,
	handleResetFilters,
	isCreatable = false,
	handleAdd,
	onTaskClick,
	onMilestoneClick,
}: IGanttTaskTableProps) => {
	const ganttRef = useRef<GanttHandle>(null);

	const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < VIEW_SWITCHER_BREAKPOINT);

	useEffect(() => {
		const handleResize = () => {
			setIsSmallScreen(window.innerWidth < VIEW_SWITCHER_BREAKPOINT);
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const [selectedViewMode, setSelectedViewMode] = useState<TSelectOption<EViewMode> | null>(
		timelineViewModeOptions.find(option => option.value === EViewMode.Week) || null,
	);

	const [tasks, setTasks] = useState<Task[]>([]);

	const [isTableExpanded, setIsTableExpanded] = useState(false);
	const [isTasksList, setIsTasksList] = useState(true);
	const [isShowFilters, setIsShowFilters] = useState(isOpenFilters);
	const [isDirtyFilters, setIsDirtyFilters] = useState(false);

	const actionAddMenuWrapperRef = useRef<HTMLDivElement>(null);

	const [isVisibleAddActionMenu, setIsVisibleAddActionMenu] = useState(false);

	const expandTable = () => setIsTableExpanded(true);

	const closeExpandedTable = () => setIsTableExpanded(false);

	const onViewModeChange = useCallback((option: TSelectOption<EViewMode>) => {
		setSelectedViewMode(option);
	}, []);

	const toggleVisibleAddActionMenu = () => setIsVisibleAddActionMenu(prevState => !prevState);

	useOnClickOutside({ refs: [actionAddMenuWrapperRef], handler: () => setIsVisibleAddActionMenu(false) });

	useEffect(() => {
		if (isLoading) {
			return;
		}

		setTasks(data);
	}, [data, isLoading]);

	useEffect(() => {
		setIsShowFilters(isOpenFilters);
	}, [isOpenFilters]);

	const toggleTasksList = () => setIsTasksList(prevState => !prevState);

	const toggleFilters = () => setIsShowFilters(prevState => !prevState);

	const onExpanderClick = (task: Task) => {
		setTasks(tasks.map(t => (t.id === task.id ? task : t)));
	};

	const onTodayClick = () => {
		ganttRef.current?.scrollToToday();
	};

	const isDisabledClearFilters = useMemo(() => {
		return !!filters?.every(({ option }) => Array.isArray(option) ? !option.length : !option)
			&& !!dateFilters?.every(({ selected }) => !selected);
	}, [filters, dateFilters]);

	const resetFilterHandler = () => {
		if (isDisabledClearFilters) return;

		if (handleResetFilters) {
			handleResetFilters();
		} else {
			filters?.forEach(({ setState }) => {
				setState(null);
			});

			dateFilters?.forEach(({ handleChange }) => {
				handleChange(null);
			});
		}

		toggleFilters();
	};

	useEffect(() => {
		if (!filters?.length) return;

		for (let i = 0; i < filters.length; i++) {
			const { setState, defaultValue, options, type } = filters[i];

			if (defaultValue !== null) {
				if (type === TableFilterTypes.MULTISELECT) {
					const defaultValues = Array.isArray(defaultValue)
						? options.filter(option => defaultValue.some(value => value === option?.value))
						: null;

					setState(defaultValues);
				} else {
					setState(options?.find(option => option?.value === defaultValue) || null);
				}
			}
		}

		for (let i = 0; i < dateFilters.length; i++) {
			const { selectProps } = dateFilters[i];

			if (!selectProps) continue;

			const { defaultValue, setState, options } = selectProps;
			if (defaultValue !== null) {
				setState(options?.find(option => option?.value === defaultValue) || null);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const isDirtyFilters: boolean = filters?.some((filter) => {
			if (filter?.type === TableFilterTypes.MULTISELECT) {
				return Array.isArray(filter.option) && filter.option.length;
			}

			return filter.option;
		});

		const isDirtyDateFilters: boolean = dateFilters?.some((filter) => filter.selected);

		setIsDirtyFilters(isDirtyFilters || isDirtyDateFilters);
	}, [filters, dateFilters]);

	const onMultiSelectFilterChange = (setState: TSetStateTableFilterMulti) => (selectedOptions: TSelectOption[]) => {
		setState(selectedOptions);
	};

	const onSelectFilterChange = (setState: TSetStateTableFilter) => (selectedOption: TSelectOption) => {
		setState(selectedOption);
	};

	const onDateFilterChange = (setState?: TSetStateTableFilter) => (selectedOption: TSelectOption | unknown) => {
		if (setState) {
			setState(selectedOption as TSelectOption);
		}
	};

	const onClickAddMenuItem = (type: EAddTypeId) => {
		if (!handleAdd) {
			return;
		}

		handleAdd(type);
	};

	const onBarClick = (task: Task) => {
		if (task.type === EGanttTaskType.task && onTaskClick) {
			onTaskClick(task);
		}

		if (task.type === EGanttTaskType.milestone && onMilestoneClick) {
			onMilestoneClick(task);
		}
	};

	const ganttChartColumnWidth = columnWidth
		? columnWidth
		: selectedViewMode?.value === EViewMode.QuarterYear ? defaultQuarterYearColumnWidth : defaultColumnWidth;

	return (
		<div className={cn({ [styles.tableExpandedOverlay]: isTableExpanded })}>
			<div className={cn(styles.ganttContainer, { [styles.tableExpanded]: isTableExpanded })}>
				{isTableExpanded && (
					<div className={styles.tableExpandedHeader}>
						<div className={styles.tableExpandedTitle}>
							Timeline
						</div>
						<CloseButton
							className={styles.tableExpandedCloseIcon}
							onClick={closeExpandedTable}
						/>
					</div>
				)}
				<div className={styles.ganttActions}>
					<div className={styles.leftActions}>
						{search && (
							<MultiSearch
								isOpenSearch={isOpenSearch}
								search={search}
								searchThreshold={searchThreshold}
								searchWidth={searchWidth}
							/>
						)}
						{!isWithoutFilter
							&& <div
								className={styles.filterIcon}
								onClick={toggleFilters}
							>
								<DynamicIconWrapper
									icon={Icons.filter}
									isActive={isShowFilters}
									isDirty={isDirtyFilters && !isShowFilters}
								/>
							</div>
						}
					</div>
					{isCreatable
						&& <div
							ref={actionAddMenuWrapperRef}
							className={styles.addButtonContainer}
							onClick={toggleVisibleAddActionMenu}
						>
							<Button
								icon={Icons.plus()}
								secondary
								text='Add new'
							/>
							<ActionsMenu
								actions={addActions}
								isVisibleMenu={isVisibleAddActionMenu}
								minWidth={128}
								onClick={onClickAddMenuItem}
							/>
						</div>
					}
					<div className={styles.rightActions}>
						<Button
							className={styles.todayScrollButton}
							onClick={onTodayClick}
							secondary
							text='Today'
							withoutBackground
						/>
						<div className={styles.viewModeSelect}>
							{!isSmallScreen
								&& <Switcher
									handleChange={onViewModeChange}
									options={timelineViewModeOptions}
									value={selectedViewMode}
								/>
							}
							{isSmallScreen
								&& <Select
									handleChange={onViewModeChange}
									isWithoutUseForms
									name='viewMode'
									options={timelineViewModeOptions}
									value={selectedViewMode}
									width='110px'
								/>
							}
						</div>
						<div className={styles.isTasksListCheckbox}>
							<SliderCheckbox
								checked={isTasksList}
								id='tasksListCheckbox'
								isLabelAfter
								label='Tasks list'
								onChange={toggleTasksList}
							/>
						</div>
						{!isTableExpanded
							&& <div className={styles.expandButton}>
								<IconButton
									icon={Icons.expandIcon}
									onClick={expandTable}
									tooltipLabel='Expand'
								/>
							</div>
						}
					</div>
				</div>
				{isShowFilters &&
					<div className={styles.ganttFilters}>
						{filters?.map(({
							disabled = false,
							name,
							placeholder,
							option,
							options,
							width,
							minWidth,
							maxWidth,
							maxMenuHeight,
							setState,
							isFilterStyle = true,
							type,
						}) => {
							if (type === TableFilterTypes.MULTISELECT) {
								return (
									<React.Fragment key={name}>
										<Select
											disabled={disabled}
											handleChange={onMultiSelectFilterChange(setState)}
											isClearable={isFilterStyle}
											isFilterStyle={isFilterStyle}
											isMulti
											isWithoutUseForms
											maxMenuHeight={maxMenuHeight}
											minWidth={minWidth}
											name={name}
											options={options}
											placeholder={placeholder}
											value={option}
											width={width}
										/>
									</React.Fragment>
								);
							}

							return (
								<React.Fragment key={name}>
									<Select
										disabled={disabled}
										handleChange={onSelectFilterChange(setState)}
										isClearable={isFilterStyle}
										isFilterStyle={isFilterStyle}
										isWithoutUseForms
										maxMenuHeight={maxMenuHeight}
										maxWidth={maxWidth}
										minWidth={minWidth}
										name={name}
										options={options}
										placeholder={placeholder}
										value={option}
										width={width}
									/>
								</React.Fragment>
							);
						})
						}
						{!!dateFilters && dateFilters?.map(({
							label,
							handleChange,
							selected,
							name,
							minDate,
							maxDate,
							disableFutureDates,
							selectProps,
							includeDatesFrom,
						}) => {
							return (
								<div
									key={name}
									className={styles.dateFilter}
								>
									{label && selectProps
										&& <Label
											className={styles.selectDatepickerLabel}
											label={label}
										/>
									}
									{selectProps
										&& <Select
											className={styles.select}
											handleChange={onDateFilterChange(selectProps?.setState)}
											isFilter
											isWithoutUseForms
											maxWidth={selectProps?.maxWidth}
											minWidth={selectProps?.minWidth}
											name={selectProps?.name}
											options={selectProps.options}
											placeholder={selectProps.placeholder}
											value={selectProps.option}
											width={selectProps?.width}
										/>
									}
									<Datepicker
										key={name}
										disableFutureDates={disableFutureDates}
										handleChange={handleChange as never}
										includeDates={includeDatesFrom}
										isClearable={isClearableDatepicker}
										isLeftPlaceholder
										isMobileDevice={false}
										label={!selectProps ? label : undefined}
										maxDate={maxDate}
										minDate={minDate}
										selected={selected}
									/>
								</div>
							);
						})
						}
						<div
							className={cn(styles.resetBtn, { [styles.disabled]: isDisabledClearFilters })}
							onClick={resetFilterHandler}
						>
							<Tooltip content={resetFilterTooltipText}>
								<div>
									Clear filters
								</div>
							</Tooltip>
						</div>
					</div>
				}
				<Gantt
					ref={ganttRef}
					columnWidth={ganttChartColumnWidth}
					emptyText={emptyText}
					isLoading={isLoading}
					isTasksList={isTasksList}
					onClick={onBarClick}
					onExpanderClick={onExpanderClick}
					tasks={tasks}
					viewMode={selectedViewMode?.value}
				/>
			</div>
		</div>
	);
};

export default memo(GanttTaskTable);
